import React from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';

// Prevent browser from scrolling the viewport when MUI pickers/popups focus elements programmatically
if (typeof HTMLElement !== 'undefined' && HTMLElement.prototype.focus) {
  const originalFocus = HTMLElement.prototype.focus;
  HTMLElement.prototype.focus = function (options) {
    originalFocus.call(this, { preventScroll: true, ...options });
  };
}

// ─── Amalgamate Adventure Dark-Teal MUI Theme ────────────────────────────────
const adventureTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#3bb3ae',
      light: '#48d1cc',
      dark: '#2a8a86',
      contrastText: '#071414',
    },
    background: {
      default: '#071414',
      paper: '#0c1a1a',
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.55)',
      disabled: 'rgba(255, 255, 255, 0.3)',
    },
    divider: 'rgba(255, 255, 255, 0.08)',
    action: {
      hover: 'rgba(59, 179, 174, 0.1)',
      selected: 'rgba(72, 209, 204, 0.18)',
      disabled: 'rgba(255, 255, 255, 0.25)',
    },
  },
  typography: {
    fontFamily: "'Inter', sans-serif",
    fontSize: 14,
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiTextField: {
      defaultProps: { size: 'small', fullWidth: true },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.04)',
          borderRadius: '10px',
          fontFamily: "'Inter', sans-serif",
          fontSize: '14px',
          color: '#ffffff',
          transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
          '& fieldset': {
            borderColor: 'rgba(255, 255, 255, 0.1)',
            transition: 'border-color 0.2s ease',
          },
          '&:hover fieldset': {
            borderColor: 'rgba(255, 255, 255, 0.25)',
          },
          '&.Mui-focused fieldset': {
            borderColor: '#3bb3ae',
            borderWidth: '1.5px',
          },
          '&.Mui-focused': {
            backgroundColor: 'rgba(255, 255, 255, 0.07)',
            boxShadow: '0 0 0 2px rgba(59, 179, 174, 0.15)',
          },
        },
        input: {
          color: '#ffffff',
          padding: '11px 14px',
          '&::placeholder': {
            color: 'rgba(255, 255, 255, 0.3)',
            opacity: 1,
          },
        },
        adornedEnd: { paddingRight: '8px' },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: 'rgba(255, 255, 255, 0.45)',
          fontFamily: "'Inter', sans-serif",
          fontSize: '13px',
          '&.Mui-focused': { color: '#3bb3ae' },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: 'rgba(255, 255, 255, 0.45)',
          '&:hover': {
            backgroundColor: 'rgba(59, 179, 174, 0.1)',
            color: '#48d1cc',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#0c1a1a',
          backgroundImage: 'none',
          border: '1px solid rgba(72, 209, 204, 0.2)',
          borderRadius: '14px',
          boxShadow: '0 16px 48px rgba(0, 0, 0, 0.6)',
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          fontFamily: "'Inter', sans-serif",
          fontSize: '13px',
          borderRadius: '8px',
          '&:hover': { backgroundColor: 'rgba(59, 179, 174, 0.15)' },
          '&.Mui-selected': {
            backgroundColor: '#3bb3ae !important',
            color: '#071414',
            fontWeight: 700,
          },
          '&.Mui-selected:hover': { backgroundColor: '#48d1cc !important' },
          '&.MuiPickersDay-today': {
            border: '1.5px solid rgba(59, 179, 174, 0.5)',
          },
        },
      },
    },
    MuiClockNumber: {
      styleOverrides: {
        root: {
          color: 'rgba(255, 255, 255, 0.7)',
          fontFamily: "'Inter', sans-serif",
          '&.Mui-selected': { backgroundColor: '#3bb3ae', color: '#071414' },
        },
      },
    },
    MuiClockPointer: {
      styleOverrides: {
        root: { backgroundColor: '#3bb3ae' },
        thumb: { backgroundColor: '#3bb3ae', border: '4px solid #3bb3ae' },
      },
    },
    MuiClock: {
      styleOverrides: {
        pin: { backgroundColor: '#3bb3ae' },
        clock: { backgroundColor: 'rgba(255, 255, 255, 0.03)' },
      },
    },
    MuiPickersYear: {
      styleOverrides: {
        yearButton: {
          color: 'rgba(255, 255, 255, 0.7)',
          fontFamily: "'Inter', sans-serif",
          '&.Mui-selected': { backgroundColor: '#3bb3ae', color: '#071414', fontWeight: 700 },
          '&:hover': { backgroundColor: 'rgba(59, 179, 174, 0.12)' },
        },
      },
    },
    MuiPickersMonth: {
      styleOverrides: {
        monthButton: {
          color: 'rgba(255, 255, 255, 0.7)',
          fontFamily: "'Inter', sans-serif",
          '&.Mui-selected': { backgroundColor: '#3bb3ae', color: '#071414', fontWeight: 700 },
          '&:hover': { backgroundColor: 'rgba(59, 179, 174, 0.12)' },
        },
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        label: {
          color: '#ffffff',
          fontFamily: "'Inter', sans-serif",
          fontWeight: 700,
          fontSize: '15px',
        },
        switchViewButton: { color: 'rgba(255, 255, 255, 0.5)' },
      },
    },
    MuiDayCalendar: {
      styleOverrides: {
        weekDayLabel: {
          color: 'rgba(59, 179, 174, 0.8)',
          fontFamily: "'Inter', sans-serif",
          fontWeight: 700,
          fontSize: '12px',
        },
      },
    },
    MuiPickersLayout: {
      styleOverrides: {
        root: { backgroundColor: '#0c1a1a' },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: "'Inter', sans-serif",
          fontWeight: 700,
          textTransform: 'none',
        },
        text: {
          color: '#48d1cc',
          '&:hover': { backgroundColor: 'rgba(72, 209, 204, 0.1)' },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: 'rgba(255, 255, 255, 0.08)' },
      },
    },
  },
});

// ─── Shared wrapper ───────────────────────────────────────────────────────────
function AdventurePickerWrapper({ children }) {
  return (
    <ThemeProvider theme={adventureTheme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        {children}
      </LocalizationProvider>
    </ThemeProvider>
  );
}

// ─── AdventureDatePicker ──────────────────────────────────────────────────────
/**
 * @param {string}   label      - Field label (default: 'Date')
 * @param {string}   value      - Date string YYYY-MM-DD
 * @param {Function} onChange   - Called with YYYY-MM-DD string (or '' on clear)
 * @param {string}   minDate    - Min selectable YYYY-MM-DD
 * @param {string}   maxDate    - Max selectable YYYY-MM-DD
 * @param {boolean}  required
 * @param {boolean}  disabled
 */
export function AdventureDatePicker({
  label = 'Date',
  value,
  onChange,
  minDate,
  maxDate,
  required = false,
  disabled = false,
}) {
  const dayjsValue = value ? dayjs(value) : null;
  const dayjsMin = minDate ? dayjs(minDate) : undefined;
  const dayjsMax = maxDate ? dayjs(maxDate) : undefined;

  const handleChange = (dayjsObj) => {
    if (!dayjsObj || !dayjsObj.isValid()) onChange('');
    else onChange(dayjsObj.format('YYYY-MM-DD'));
  };

  return (
    <AdventurePickerWrapper>
      <DatePicker
        label={required ? `${label} *` : label}
        value={dayjsValue}
        onChange={handleChange}
        minDate={dayjsMin}
        maxDate={dayjsMax}
        disabled={disabled}
        slotProps={{
          textField: { fullWidth: true, size: 'small' },
          popper: {
            modifiers: [
              { name: 'preventOverflow', options: { boundary: 'window' } },
              { name: 'flip', options: { altBoundary: true } }
            ],
          },
        }}
      />
    </AdventurePickerWrapper>
  );
}

// ─── AdventureTimePicker ──────────────────────────────────────────────────────
/**
 * @param {string}   label      - Field label (default: 'Time')
 * @param {string}   value      - Time string HH:mm
 * @param {Function} onChange   - Called with HH:mm string (or '' on clear)
 * @param {boolean}  required
 * @param {boolean}  disabled
 */
export function AdventureTimePicker({
  label = 'Time',
  value,
  onChange,
  required = false,
  disabled = false,
}) {
  const dayjsValue = value
    ? dayjs(`${dayjs().format('YYYY-MM-DD')}T${value}`)
    : null;

  const handleChange = (dayjsObj) => {
    if (!dayjsObj || !dayjsObj.isValid()) onChange('');
    else onChange(dayjsObj.format('HH:mm'));
  };

  return (
    <AdventurePickerWrapper>
      <TimePicker
        label={required ? `${label} *` : label}
        value={dayjsValue}
        onChange={handleChange}
        disabled={disabled}
        slotProps={{
          textField: { fullWidth: true, size: 'small' },
          popper: {
            modifiers: [
              { name: 'preventOverflow', options: { boundary: 'window' } },
              { name: 'flip', options: { altBoundary: true } }
            ],
          },
        }}
      />
    </AdventurePickerWrapper>
  );
}

// ─── AdventureDateTimePickerCombined ─────────────────────────────────────────
/**
 * @param {string}   label      - Field label (default: 'Date & Time')
 * @param {string}   value      - ISO datetime string (or '')
 * @param {Function} onChange   - Called with ISO string (or '' on clear)
 * @param {string}   minDate    - Min selectable YYYY-MM-DD
 * @param {boolean}  required
 * @param {boolean}  disabled
 */
export function AdventureDateTimePickerCombined({
  label = 'Date & Time',
  value,
  onChange,
  minDate,
  required = false,
  disabled = false,
}) {
  const dayjsValue = value ? dayjs(value) : null;
  const dayjsMin = minDate ? dayjs(minDate) : undefined;

  const handleChange = (dayjsObj) => {
    if (!dayjsObj || !dayjsObj.isValid()) onChange('');
    else onChange(dayjsObj.toISOString());
  };

  return (
    <AdventurePickerWrapper>
      <DateTimePicker
        label={required ? `${label} *` : label}
        value={dayjsValue}
        onChange={handleChange}
        minDate={dayjsMin}
        disabled={disabled}
        slotProps={{
          textField: { fullWidth: true, size: 'small' },
          popper: {
            modifiers: [
              { name: 'preventOverflow', options: { boundary: 'window' } },
              { name: 'flip', options: { altBoundary: true } }
            ],
          },
        }}
      />
    </AdventurePickerWrapper>
  );
}

// ─── Default Export: AdventureDateTimePicker ─────────────────────────────────
/**
 * Unified Date and/or Time Picker component
 * @param {string}   type       - 'date' | 'time' | 'datetime'
 * @param {string}   label      - Field label
 * @param {string}   value      - Value string (YYYY-MM-DD for date, HH:mm for time, ISO for datetime)
 * @param {Function} onChange   - Value change callback
 * @param {string}   minDate    - Min date constraint (YYYY-MM-DD)
 * @param {string}   maxDate    - Max date constraint (YYYY-MM-DD)
 * @param {boolean}  required
 * @param {boolean}  disabled
 */
export default function AdventureDateTimePicker({
  type = 'date',
  label,
  value,
  onChange,
  minDate,
  maxDate,
  required = false,
  disabled = false,
}) {
  if (type === 'time') {
    return (
      <AdventureTimePicker
        label={label || 'Time'}
        value={value}
        onChange={onChange}
        required={required}
        disabled={disabled}
      />
    );
  }
  if (type === 'datetime') {
    return (
      <AdventureDateTimePickerCombined
        label={label || 'Date & Time'}
        value={value}
        onChange={onChange}
        minDate={minDate}
        required={required}
        disabled={disabled}
      />
    );
  }
  // Default to date
  return (
    <AdventureDatePicker
      label={label || 'Date'}
      value={value}
      onChange={onChange}
      minDate={minDate}
      maxDate={maxDate}
      required={required}
      disabled={disabled}
    />
  );
}

